/**
 * Share Dialogs — Extracted from ShareButtons.jsx
 * Contains: RepostDialog, CustomUrlDialog, RedditShareDialog, QRDialog
 */
import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose
} from '../ui/dialog';
import { toast } from 'sonner';
import {
  RefreshCw, ExternalLink, Search, TrendingUp, SortAsc, QrCode
} from 'lucide-react';
import QRCodeInline from './QRCodeInline';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Reddit icon component
export const RedditIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
  </svg>
);

// Quality badge for subreddits
const QualityBadge = ({ quality }) => {
  const config = {
    verified: { icon: '✓', color: 'text-green-500', title: 'Verified' },
    high: { icon: '●', color: 'text-blue-500', title: 'High quality' },
    medium: { icon: '●', color: 'text-amber-500', title: 'Medium quality' },
    low: { icon: '●', color: 'text-gray-400', title: 'Unverified' },
  };
  const c = config[quality] || config.medium;
  return <span className={`${c.color} text-xs`} title={c.title}>{c.icon}</span>;
};

export const RepostDialog = ({ open, onOpenChange, title, description, onRepost, sharing }) => {
  const [comment, setComment] = useState('');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5" /> Repost with Comment
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Add your thoughts... (optional)"
            rows={3}
            data-testid="repost-comment-input"
          />
          <div className="p-3 bg-muted rounded-lg text-sm">
            <p className="font-medium truncate">{title}</p>
            {description && <p className="text-muted-foreground truncate">{description}</p>}
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
          <Button
            onClick={() => { onRepost(comment); setComment(''); }}
            disabled={sharing}
            data-testid="repost-submit"
          >
            {sharing ? 'Posting...' : 'Repost'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const CustomUrlDialog = ({ open, onOpenChange, onSubmit }) => {
  const [customUrl, setCustomUrl] = useState('');

  const handleSubmit = () => {
    if (!customUrl.trim()) { toast.error('Please enter a URL'); return; }
    try {
      new URL(customUrl);
      onSubmit(customUrl);
      setCustomUrl('');
    } catch { toast.error('Please enter a valid URL'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ExternalLink className="w-5 h-5" /> Share to Custom URL
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Enter the URL where you want to share this content:</label>
            <Input value={customUrl} onChange={(e) => setCustomUrl(e.target.value)} placeholder="https://example.com/share" type="url" data-testid="custom-url-input" />
            <p className="text-xs text-muted-foreground mt-1">The link will be copied to your clipboard when the page opens</p>
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
          <Button onClick={handleSubmit} data-testid="custom-url-submit">Open & Share</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const RedditShareDialog = ({ open, onOpenChange, title: initialTitle, onSubmit, petType }) => {
  const [redditTitle, setRedditTitle] = useState(initialTitle || '');
  const [redditSubreddit, setRedditSubreddit] = useState('');
  const [subredditSearch, setSubredditSearch] = useState('');
  const [subreddits, setSubreddits] = useState([]);
  const [subredditSort, setSubredditSort] = useState('popularity');
  const [loadingSubreddits, setLoadingSubreddits] = useState(false);

  useEffect(() => { setRedditTitle(initialTitle || ''); }, [initialTitle]);

  useEffect(() => {
    if (!open) return;
    const fetchSubreddits = async () => {
      setLoadingSubreddits(true);
      try {
        let url = `${API_URL}/api/subreddits?sort_by=${subredditSort}&limit=20`;
        if (petType) url += `&pet_type=${petType}`;
        const res = await fetch(url);
        if (res.ok) setSubreddits(await res.json());
      } catch (err) {  }
      finally { setLoadingSubreddits(false); }
    };
    fetchSubreddits();
  }, [open, subredditSort, petType]);

  const filtered = subredditSearch
    ? subreddits.filter(s => s.name.toLowerCase().includes(subredditSearch.toLowerCase()) || s.description?.toLowerCase().includes(subredditSearch.toLowerCase()))
    : subreddits;

  const handleSubmit = () => {
    const sub = redditSubreddit.trim().replace(/^r\//, '');
    if (!sub) { toast.error('Please select or enter a subreddit'); return; }
    onSubmit(sub, redditTitle.trim() || initialTitle || 'Check this out!');
    setRedditSubreddit('');
    setRedditTitle('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RedditIcon className="w-5 h-5 text-[#FF4500]" /> Post to Reddit
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Post Title</label>
            <Input value={redditTitle} onChange={(e) => setRedditTitle(e.target.value)} placeholder={initialTitle || 'Enter a title for your post'} />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium">Choose Subreddit</label>
              <div className="flex gap-1">
                <Button variant={subredditSort === 'popularity' ? 'secondary' : 'ghost'} size="sm" className="h-7 px-2 text-xs" onClick={() => setSubredditSort('popularity')}>
                  <TrendingUp className="w-3 h-3 mr-1" /> Popular
                </Button>
                <Button variant={subredditSort === 'alphabetical' ? 'secondary' : 'ghost'} size="sm" className="h-7 px-2 text-xs" onClick={() => setSubredditSort('alphabetical')}>
                  <SortAsc className="w-3 h-3 mr-1" /> A-Z
                </Button>
              </div>
            </div>
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input value={redditSubreddit} onChange={(e) => setRedditSubreddit(e.target.value)} placeholder="r/subreddit or search below" className="pl-10" />
            </div>
            <div className="max-h-48 overflow-y-auto space-y-1 border rounded-md p-1">
              {loadingSubreddits ? (
                <div className="text-center py-4 text-muted-foreground text-sm">Loading...</div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground text-sm">No subreddits found. Type a custom one above.</div>
              ) : filtered.map((sub) => (
                sub.status !== 'not_allowed' && (
                  <button key={sub.id || sub.name} type="button"
                    className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center gap-2 ${redditSubreddit === sub.name ? 'bg-[#FF4500] text-white' : 'hover:bg-muted'}`}
                    onClick={() => setRedditSubreddit(sub.name)}>
                    <QualityBadge quality={sub.quality} />
                    <div className="flex-1 min-w-0">
                      <span className="font-medium">{sub.name}</span>
                      {sub.share_count > 0 && <span className="text-xs ml-1 opacity-60">({sub.share_count})</span>}
                      {sub.description && <span className="text-xs ml-2 opacity-70 truncate block">{sub.description}</span>}
                    </div>
                  </button>
                )
              ))}
            </div>
          </div>
          <p className="text-xs text-muted-foreground">Note: Reddit will require you to log in to post.</p>
        </div>
        <DialogFooter>
          <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
          <Button onClick={handleSubmit} className="bg-[#FF4500] hover:bg-[#FF4500]/90">Open Reddit</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const QRDialog = ({ open, onOpenChange, url, title, source }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-sm">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <QrCode className="w-5 h-5" /> QR Code
        </DialogTitle>
      </DialogHeader>
      <QRCodeInline url={url} title={title || 'Scan to view'} source={source} />
    </DialogContent>
  </Dialog>
);
